/**
 * Backup Usage Alerts API (End Customer)
 *
 * APIs para end customers gerenciarem alertas de uso excessivo de backup.
 */

import api from './api'

// ============================================================================
// Types
// ============================================================================

export type AlertType = 'excessive_growth' | 'rapid_increase' | 'anomaly_detected' | 'quota_warning'
export type AlertSeverity = 'info' | 'warning' | 'critical'
export type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'dismissed'

export type BackupUsageAlert = {
  id: string
  vm_id: string | null
  vm_name: string | null
  alert_type: AlertType
  severity: AlertSeverity
  status: AlertStatus
  title: string
  message: string
  details: {
    vm_id?: string
    vm_name?: string
    baseline_gb?: number
    current_gb?: number
    growth_percentage?: number
    threshold?: number
    period_days?: number
  }
  baseline_value: number | null
  current_value: number | null
  change_percentage: number | null
  threshold_exceeded: number | null
  detected_at: string
  notification_sent: boolean
  acknowledged_at: string | null
  resolved_at: string | null
  resolution_notes: string | null
  created_at: string
}

export type AlertsListResponse = {
  alerts: BackupUsageAlert[]
  total: number
  skip: number
  limit: number
}

export type AlertActionResponse = {
  message: string
  alert_id: string
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Lista alertas de uso de backup
 */
export const listBackupAlerts = async (params?: {
  status_filter?: AlertStatus
  severity_filter?: AlertSeverity
  skip?: number
  limit?: number
}): Promise<AlertsListResponse> => {
  const response = await api.get('/api/v1/backup-usage/alerts', { params })
  return response.data
}

/**
 * Reconhece um alerta (marca como acknowledged)
 */
export const acknowledgeAlert = async (alertId: string): Promise<AlertActionResponse> => {
  const response = await api.post(`/api/v1/backup-usage/alerts/${alertId}/acknowledge`)
  return response.data
}

/**
 * Resolve um alerta com notas opcionais
 */
export const resolveAlert = async (
  alertId: string,
  resolutionNotes?: string,
): Promise<AlertActionResponse> => {
  const response = await api.post(`/api/v1/backup-usage/alerts/${alertId}/resolve`, {
    resolution_notes: resolutionNotes,
  })
  return response.data
}

/**
 * Descarta um alerta (falso positivo)
 */
export const dismissAlert = async (alertId: string): Promise<AlertActionResponse> => {
  const response = await api.post(`/api/v1/backup-usage/alerts/${alertId}/dismiss`)
  return response.data
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Retorna label traduzido do tipo de alerta
 */
export const getAlertTypeLabel = (type: AlertType): string => {
  const labels: Record<AlertType, string> = {
    excessive_growth: 'Crescimento Excessivo',
    rapid_increase: 'Aumento Rápido',
    anomaly_detected: 'Anomalia Detectada',
    quota_warning: 'Aviso de Quota',
  }
  return labels[type] || type
}

/**
 * Retorna label traduzido da severidade
 */
export const getAlertSeverityLabel = (severity: AlertSeverity): string => {
  const labels: Record<AlertSeverity, string> = {
    info: 'Informação',
    warning: 'Atenção',
    critical: 'Crítico',
  }
  return labels[severity] || severity
}

/**
 * Retorna label traduzido do status
 */
export const getAlertStatusLabel = (status: AlertStatus): string => {
  const labels: Record<AlertStatus, string> = {
    active: 'Ativo',
    acknowledged: 'Reconhecido',
    resolved: 'Resolvido',
    dismissed: 'Descartado',
  }
  return labels[status] || status
}

/**
 * Retorna cor para badge de severidade
 */
export const getAlertSeverityColor = (severity: AlertSeverity): string => {
  const colors: Record<AlertSeverity, string> = {
    info: 'bg-blue-100 text-blue-800',
    warning: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-red-100 text-red-800',
  }
  return colors[severity] || 'bg-gray-100 text-gray-800'
}

/**
 * Retorna cor para badge de status
 */
export const getAlertStatusColor = (status: AlertStatus): string => {
  const colors: Record<AlertStatus, string> = {
    active: 'bg-orange-100 text-orange-800',
    acknowledged: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Retorna ícone emoji para severidade
 */
export const getAlertSeverityIcon = (severity: AlertSeverity): string => {
  const icons: Record<AlertSeverity, string> = {
    info: 'ℹ️',
    warning: '⚠️',
    critical: '🔴',
  }
  return icons[severity] || '📊'
}

/**
 * Formata data ISO para exibição
 */
export const formatAlertDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/**
 * Formata porcentagem de crescimento
 */
export const formatGrowthPercentage = (percentage: number | null): string => {
  if (percentage === null || percentage === undefined) return '-'
  return `+${percentage.toFixed(1)}%`
}

/**
 * Formata storage em GB
 */
export const formatStorageGB = (gb: number | null): string => {
  if (gb === null || gb === undefined) return '-'
  return `${gb.toFixed(2)} GB`
}

// ============================================================================
// Export default object
// ============================================================================

const backupAlertsApi = {
  listBackupAlerts,
  acknowledgeAlert,
  resolveAlert,
  dismissAlert,
  // Helper functions
  getAlertTypeLabel,
  getAlertSeverityLabel,
  getAlertStatusLabel,
  getAlertSeverityColor,
  getAlertStatusColor,
  getAlertSeverityIcon,
  formatAlertDate,
  formatGrowthPercentage,
  formatStorageGB,
}

export default backupAlertsApi
